import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TopCandidates } from '../components/TopCandidates';
import { TalentHeatmap } from '../components/TalentHeatmap';
import { CandidateClusters } from '../components/CandidateClusters';
import {
  BarChart3, Trophy, MapPin, Layers, TrendingUp, Brain
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  CartesianGrid
} from 'recharts';
import { toast } from 'sonner';
import {
  getJobs, getCandidates, getTopCandidates, getLocationHeatmap, getCandidateClusters
} from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { useRegion } from '../context/RegionContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-foreground">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs text-muted-foreground">
          {p.name}: <span className="font-semibold text-foreground">{Math.round(p.value)}</span>
        </p>
      ))}
    </div>
  );
};

export default function AnalyticsPage() {
  const { theme } = useTheme();
  const { region } = useRegion();
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [topCandidates, setTopCandidates] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => { loadJobs(); }, [region]);

  useEffect(() => {
    if (selectedJobId) loadJobData(selectedJobId);
  }, [selectedJobId]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const data = await getJobs(region);
      const withCandidates = data.filter(j => j.candidate_total > 0);
      const list = withCandidates.length ? withCandidates : data.slice(0, 50);
      setJobs(list);
      if (list.length > 0) setSelectedJobId(list[0].id);
    } catch (e) { toast.error('Failed to load jobs'); }
    finally { setLoading(false); }
  };

  const loadJobData = async (jid) => {
    try {
      setLoadingData(true);
      const [cands, top, hm, cl] = await Promise.all([
        getCandidates(jid),
        getTopCandidates(jid, 10),
        getLocationHeatmap(jid),
        getCandidateClusters(jid)
      ]);
      setCandidates(cands);
      setTopCandidates(top.top_candidates || []);
      setHeatmapData(hm.heatmap || []);
      setClusters(cl.clusters || []);
    } catch (e) { console.error(e); }
    finally { setLoadingData(false); }
  };

  // Build skill radar for top candidates
  const analyzed = candidates.filter(c => c.analysis);
  const avgSkills = {};
  analyzed.forEach(c => {
    const a = c.analysis || {};
    ['skill_fit', 'role_readiness', 'cultural_fit', 'stability', 'growth_potential'].forEach(key => {
      if (a[key]) { avgSkills[key] = (avgSkills[key] || []).concat(a[key]); }
    });
  });
  const radarData = Object.entries(avgSkills).map(([key, vals]) => ({
    skill: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    avg: vals.reduce((a, b) => a + b, 0) / vals.length
  }));

  // Score histogram
  const scoreBuckets = [
    { range: '0-20', count: 0 }, { range: '21-40', count: 0 },
    { range: '41-60', count: 0 }, { range: '61-80', count: 0 },
    { range: '81-100', count: 0 }
  ];
  analyzed.forEach(c => {
    const s = c.final_score || 0;
    if (s <= 20) scoreBuckets[0].count++;
    else if (s <= 40) scoreBuckets[1].count++;
    else if (s <= 60) scoreBuckets[2].count++;
    else if (s <= 80) scoreBuckets[3].count++;
    else scoreBuckets[4].count++;
  });

  const gridColor = theme === 'dark' ? '#1e293b' : '#e2e8f0';
  const axisColor = theme === 'dark' ? '#64748b' : '#94a3b8';

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 gap-6"><Skeleton className="h-72" /><Skeleton className="h-72" /></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Analytics
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Deep insights into your talent pipeline</p>
        </div>
        <Select value={selectedJobId || ''} onValueChange={setSelectedJobId}>
          <SelectTrigger className="w-[280px]" data-testid="analytics-job-select">
            <SelectValue placeholder="Select a job" />
          </SelectTrigger>
          <SelectContent>
            {jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!selectedJobId ? (
        <Card><CardContent className="py-16 text-center">
          <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
          <p>Select a job to view analytics</p>
        </CardContent></Card>
      ) : loadingData ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-72" />)}
        </div>
      ) : (
        <>
          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Score Distribution */}
            <Card data-testid="analytics-score-dist">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <BarChart3 className="h-4 w-4 text-indigo-500" strokeWidth={1.8} />
                  Score Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analyzed.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={scoreBuckets} barSize={36}>
                      <CartesianGrid vertical={false} stroke={gridColor} />
                      <XAxis dataKey="range" tick={{ fontSize: 12, fill: axisColor }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: axisColor }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" name="Candidates" radius={[6, 6, 0, 0]} fill="#6366f1" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[240px] flex items-center justify-center text-sm text-muted-foreground">No analyzed candidates</div>
                )}
              </CardContent>
            </Card>

            {/* Skill Radar */}
            <Card data-testid="analytics-skill-radar">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Brain className="h-4 w-4 text-cyan-500" strokeWidth={1.8} />
                  Average Skill Profile
                </CardTitle>
              </CardHeader>
              <CardContent>
                {radarData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <RadarChart data={radarData}>
                      <PolarGrid stroke={gridColor} />
                      <PolarAngleAxis dataKey="skill" tick={{ fontSize: 10, fill: axisColor }} />
                      <PolarRadiusAxis tick={{ fontSize: 10, fill: axisColor }} domain={[0, 100]} />
                      <Radar name="Average" dataKey="avg" stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.2} strokeWidth={2} />
                      <Tooltip content={<CustomTooltip />} />
                    </RadarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[240px] flex items-center justify-center text-sm text-muted-foreground">No skill data available</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Tabs for detailed views */}
          <Tabs defaultValue="top10" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-4">
              <TabsTrigger value="top10" className="gap-2" data-testid="analytics-tab-top10">
                <Trophy className="h-4 w-4" /> Top 10
              </TabsTrigger>
              <TabsTrigger value="clusters" className="gap-2" data-testid="analytics-tab-clusters">
                <Layers className="h-4 w-4" /> Clusters
              </TabsTrigger>
              <TabsTrigger value="heatmap" className="gap-2" data-testid="analytics-tab-heatmap">
                <MapPin className="h-4 w-4" /> Heatmap
              </TabsTrigger>
            </TabsList>
            <TabsContent value="top10">
              <TopCandidates topCandidates={topCandidates} isLoading={loadingData} onViewCandidate={() => {}} />
            </TabsContent>
            <TabsContent value="clusters">
              <CandidateClusters clusters={clusters} isLoading={loadingData} />
            </TabsContent>
            <TabsContent value="heatmap">
              <TalentHeatmap heatmapData={heatmapData} isLoading={loadingData} />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
